// src/ConsumerApp/Screens/Bucket/Bucket.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import BucketItemCard from './components/BucketItemCard';
import DeliveryAddressModal from '../Addresses/DeliveryAddressModal';
import { useAuthModal } from '../../context/AuthModalContext';
import { guestBucketManager } from '../../utils/guestBucketManager';

// SVGs
import X from '../../../assets/icons/X.svg';
import SealPercent from '../../../assets/icons/SealPercent.svg';
import WalletIcon from '../../../assets/icons/Wallet.svg';
import Clock from '../../../assets/icons/Clock.svg';
import Truck from '../../../assets/icons/Truck.svg';
import ShieldCheck from '../../../assets/icons/ShieldCheck.svg';
import Lightning from '../../../assets/icons/Lightning.svg';
import ArrowRight from '../../../assets/icons/ArrowRight.svg';
import bucket from '../../../assets/icons/bucketConcrete.svg';

// Shared API Hooks
import {
  useGetBucketItemsAndBillSummary,
  useUpdateBucketItemQuantity,
  useDeleteBucketItem,
} from '../../api/products/product.hooks';
import { useGetDefaultDeliveryAddress } from '../../api/projects/project.hooks';
import { useEvaluateDiscounts, useApplyDiscount, useRemoveDiscount } from '../../api/offers/discounts.hooks';
import { useGetWalletBalance, useApplyWalletBalance } from '../../api/wallet/wallet.hooks';
import { useInitiatePayment, useVerifyPaymentStatus, useGetOrderByTransactionId } from '../../api/payment/payment.hooks';
import { load } from '@cashfreepayments/cashfree-js';
import { useUpdateCustomerRegistration } from '../../api/profile/profile.hooks'; // <-- Import the new hook

export default function Bucket({ isOpen, onClose }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initiatePaymentMutation = useInitiatePayment();
  const verifyPaymentMutation = useVerifyPaymentStatus();
  const getOrderMutation = useGetOrderByTransactionId();
  const [isNavigating, setIsNavigating] = useState(false);
  const { openLoginModal } = useAuthModal();

  // Reactive Session State: preserves your auth while syncing on login
  const [customer, setCustomer] = useState(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const handleAuthChange = () => {
      try {
        const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
        setCustomer(stored ? JSON.parse(stored) : null);
      } catch {
        setCustomer(null);
      }
    };
    window.addEventListener('auth-state-change', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    return () => {
      window.removeEventListener('auth-state-change', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, []);

  const customerId = customer?._id || customer?.id;
  const isAuthenticated = Boolean(customerId);
  const activeTab = 'BUY_NOW';
// Initialize the verification mutation hook
  const updateRegistrationMutation = useUpdateCustomerRegistration(customer?.token); 
  
  // Reactive Guest Items
  const [guestItems, setGuestItems] = useState(() => guestBucketManager.getItems());

  useEffect(() => {
    const handleGuestBucket = () => {
      setGuestItems(guestBucketManager.getItems());
    };
    window.addEventListener('guest-bucket-change', handleGuestBucket);
    return () => window.removeEventListener('guest-bucket-change', handleGuestBucket);
  }, []);

  const guestSubtotal = useMemo(() => {
    return guestItems.reduce((acc, item) => {
      const p = Number(item.unit_price || item.price || item.selling_price || 0);
      const q = Number(item.quantity || 1);
      return acc + p * q;
    }, 0);
  }, [guestItems]);

  const [useWallet, setUseWallet] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('ONLINE');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [evaluatedDiscounts, setEvaluatedDiscounts] = useState([]);
  const [isEvaluatingDiscounts, setIsEvaluatingDiscounts] = useState(false);
  
  // Brick Points Unlock Modal State
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
  const [profType, setProfType] = useState('');
  const [gstin, setGstin] = useState('');

  // Scroll locking
  useEffect(() => {
    if (isOpen) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;

      document.body.style.overflow = 'hidden';
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);

  // Data fetching for authenticated users
  const { data: rawAddressRes, isLoading: isAddressLoading } = useGetDefaultDeliveryAddress(customerId);
  const deliveryAddress = useMemo(() => {
    if (!rawAddressRes) return null;
    return rawAddressRes.data?.address || rawAddressRes.data || rawAddressRes.address || rawAddressRes;
  }, [rawAddressRes]);

  const currentAddressId = deliveryAddress?._id || deliveryAddress?.delivery_address_id || null;

  const {
    data: bucketRes,
    refetch: refetchBucketData,
  } = useGetBucketItemsAndBillSummary({
    customer_id: customerId,
    purchase_type: activeTab,
    delivery_address_id: currentAddressId,
  });
    //console.log('bucketRes------',bucketRes)
  const { data: walletData } = useGetWalletBalance(customerId);
  const availableWalletBalance = walletData?.available_wallet_balance || 0;

  const updateItemMutation = useUpdateBucketItemQuantity();
  const deleteItemMutation = useDeleteBucketItem();
  const applyWalletMutation = useApplyWalletBalance();
  const evaluateDiscountsMutation = useEvaluateDiscounts();
  const applyDiscountMutation = useApplyDiscount();
  const removeDiscountMutation = useRemoveDiscount();

  // Unified items extraction across backend structures
  const rawBucketData = bucketRes?.bucket_data || bucketRes?.data?.bucket_data || bucketRes?.data || bucketRes || {};
  const serverItems = useMemo(() => {
    if (Array.isArray(rawBucketData?.items)) return rawBucketData.items;
    if (Array.isArray(rawBucketData?.buy_now_bucket?.items)) return rawBucketData.buy_now_bucket.items;
    if (Array.isArray(bucketRes?.items)) return bucketRes.items;
    return [];
  }, [rawBucketData, bucketRes]);

  // Brick Points Extractions
  const brickPointsEarned = rawBucketData?.brick_points_earned || 0;
  const brickPointsEligible =  rawBucketData?.brick_points_eligible === true ; 
  const brickPointsLocked = rawBucketData?.brick_points_locked !== false; // defaults to true

  // Unified items list: Server when logged in, Guest items when unauthenticated
  const displayItems = isAuthenticated ? serverItems : guestItems;

  const billSummary = useMemo(() => {
    return rawBucketData?.bill_summary || rawBucketData?.buy_now_bucket?.bill_summary || {};
  }, [rawBucketData]);

  const deliveryOptions = Array.isArray(rawBucketData?.delivery_options) ? rawBucketData.delivery_options : [];
  const deliveryServiceable = rawBucketData?.delivery_serviceable !== false;
  const deliveryFailureMessage = rawBucketData?.delivery_failure_message || 'Delivery is currently not available for this site.';

  const serverSelectedDeliveryType = rawBucketData?.selected_delivery_type || rawBucketData?.selected_delivery_option?.type || 'FAST';
  const [selectedDeliveryType, setSelectedDeliveryType] = useState(serverSelectedDeliveryType);

  useEffect(() => {
    if (rawBucketData?.selected_delivery_type || rawBucketData?.selected_delivery_option?.type) {
      setSelectedDeliveryType(serverSelectedDeliveryType);
    }
  }, [rawBucketData, serverSelectedDeliveryType]);

  const selectedDeliveryOption =
    deliveryOptions.find((o) => o.type === selectedDeliveryType) ||
    rawBucketData?.selected_delivery_option ||
    deliveryOptions[0];

  const serverDeliveryCharge = Number(billSummary?.delivery_charge) || 0;
  const selectedDeliveryCharge = Number(selectedDeliveryOption?.deliveryCharge) || 0;
  const totalPayableAmount = Number(billSummary?.total_payable) || 0;
  const selectedTotalPayable = totalPayableAmount + selectedDeliveryCharge - serverDeliveryCharge;

  const isBulkEligible = (isAuthenticated ? totalPayableAmount : guestSubtotal) >= 50000;
  const isOverLimit = (isAuthenticated ? totalPayableAmount : guestSubtotal) >= 100000;

  // Formatting address display
  const addressDisplayName = useMemo(() => {
    if (deliveryAddress?.address_type === 'project' && deliveryAddress.project_name) {
      return `Deliver to: ${deliveryAddress.project_name}`;
    }
    return 'Deliver to: My Site Address';
  }, [deliveryAddress]);

  const formattedAddress = useMemo(() => {
    if (!deliveryAddress) return 'No delivery address selected. Click change to set one.';
    const parts = [
      deliveryAddress.flat_number,
      deliveryAddress.building_name,
      deliveryAddress.area_sector_locality || deliveryAddress.complete_address,
      deliveryAddress.city,
      deliveryAddress.pincode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Address available on file';
  }, [deliveryAddress]);

  useEffect(() => {
    setUseWallet(Boolean(billSummary?.wallet_balance > 0));
  }, [billSummary?.wallet_balance]);

  const handleUpdateQty = (itemId, newQty) => {
    if (isAuthenticated) {
      updateItemMutation.mutate({
        customer_id: customerId,
        item_id: itemId,
        purchase_type: activeTab,
        quantity: newQty,
      });
    } else {
      if (newQty <= 0) {
        guestBucketManager.removeItem(itemId);
      } else {
        guestBucketManager.updateQuantity(itemId, newQty);
      }
    }
  };

  const handleRemoveItem = (itemId) => {
    if (isAuthenticated) {
      deleteItemMutation.mutate(
        { customer_id: customerId, item_id: itemId, bucket_id: rawBucketData?.bucket_id },
        {
          onSuccess: () => {
            refetchBucketData();
            queryClient.invalidateQueries({ queryKey: ['bucketSummary', customerId] });
          },
        }
      );
    } else {
      guestBucketManager.removeItem(itemId);
    }
  };

  const handleWalletToggle = async (e) => {
    const nextVal = e.target.checked;
    if (nextVal && (!availableWalletBalance || availableWalletBalance <= 0)) {
      alert('You do not have any wallet balance available to apply.');
      return;
    }
    setUseWallet(nextVal);
    try {
      await applyWalletMutation.mutateAsync({
        customer_id: customerId,
        bucket_id: rawBucketData?.bucket_id,
        apply_wallet: nextVal,
      });
      refetchBucketData();
    } catch {
      setUseWallet(!nextVal);
    }
  };

  const handleOpenDiscounts = async () => {
    setIsDiscountModalOpen(true);
    setIsEvaluatingDiscounts(true);
    try {
      const payload = {
        event: { event: 'order' },
        consumer: { customer_id: customerId, customer_type: customer?.customer_type },
        context: {
          brand: [...new Set(displayItems.map((i) => i.brand).filter(Boolean))],
          category: [...new Set(displayItems.map((i) => i.category).filter(Boolean))],
          items: displayItems.map((i) => ({ item_id: i.item_id || i.product_id, qty: i.quantity, price: i.price })),
          amount: billSummary?.item_total?.toFixed(2) || 0,
          partner_id: customer?.hubId || 'default',
        },
      };
      const res = await evaluateDiscountsMutation.mutateAsync(payload);
      setEvaluatedDiscounts(res?.data || res || []);
    } catch (err) {
      console.error('Coupon evaluation failed', err);
    } finally {
      setIsEvaluatingDiscounts(false);
    }
  };

  const handleApplyDiscount = async (discount) => {
    try {
      await applyDiscountMutation.mutateAsync({
        consumer: { customer_id: customerId },
        order: { order_id: rawBucketData?.bucket_id },
        support_requested: discount.discount_amount || 0,
        metadata: { code: discount.code, title: discount.title },
      });
      refetchBucketData();
      setIsDiscountModalOpen(false);
    } catch {
      alert('Could not apply this offer.');
    }
  };

  const handleRemoveDiscount = async (ruleCode) => {
    try {
      await removeDiscountMutation.mutateAsync({
        consumer: { customer_id: customerId },
        order: { order_id: rawBucketData?.bucket_id },
        metadata: { code: ruleCode },
      });
      refetchBucketData();
    } catch {
      alert('Could not remove this offer.');
    }
  };

  // Utility function to extract and validate PAN from GSTIN
 const GetPanDetailsByGstNumber = async (gstin) => {
  if (!gstin || typeof gstin !== 'string') return null;

  // Clean the input
  const cleanGstin = gstin.trim().toUpperCase();

  // A valid Indian GSTIN is exactly 15 characters long
  if (cleanGstin.length !== 15) return null;

  // The PAN is always characters 3 through 12 (index 2 to 12)
  const extractedPan = cleanGstin.slice(2, 12);

  // Optional: Validate the extracted PAN format (5 Letters, 4 Digits, 1 Letter)
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  if (!panRegex.test(extractedPan)) {
    return null; 
  }

  return extractedPan;
};

// --- UPDATED VERIFICATION SUBMISSION METHOD ---
  const handleVerifyPointsProfile = async () => {
    if (!profType) {
      alert('Please select your Customer Type to continue.');
      return;
    }
    
    try {
      let extractedPan = '';

      // If the user entered a GSTIN, extract the PAN
      if (gstin) {
        extractedPan = await GetPanDetailsByGstNumber(gstin);
        
        if (!extractedPan) {
          alert("Invalid GSTIN format. Please check the 15-digit number and try again.");
          return; // Stop submission if GSTIN is invalid
        }
      }

      // Hit the profile update mutation
      await updateRegistrationMutation.mutateAsync({
        _id: customerId,
        customer_type: profType,
        gstin: gstin.toUpperCase(),
        pan: extractedPan // Pass the extracted PAN to the backend
      });

      alert("Verification details submitted successfully! Your Brick Points will be unlocked upon admin approval.");
      setIsPointsModalOpen(false);
      
      // Refresh bucket to get updated customer points/locked status
      refetchBucketData(); 
    } catch (err) {
      console.error("Verification error", err);
      alert(err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
    }
  };

  const verifyBackendPaymentStatus = async (orderId) => {
    setIsProcessingPayment(true);
    try {
      const result = await verifyPaymentMutation.mutateAsync({
        order_id: orderId,
        customer_id: customerId,
      }); 
      console.log('verifyBackendPaymentStatus', result)
      const status = (result?.status || result?.data?.status || '').toUpperCase();

      if (['SUCCESS', 'COMPLETED', 'PAID'].includes(status)) {
        setPaymentStatus({
          type: 'success',
          navOrderId: orderId,
          message: 'Your payment was successful. Your order is confirmed!',
        });
        refetchBucketData();
        queryClient.invalidateQueries({ queryKey: ['customerOrders', customerId] });
      } else if (['PENDING', 'PROCESSING', 'IN_PROGRESS'].includes(status)) {
        setPaymentStatus({
          type: 'pending',
          navOrderId: orderId,
          message: 'Your payment is being processed by your bank. We will update you once confirmed.',
        });
      } else if (['CANCELLED', 'USER_DROPPED', 'ABORTED'].includes(status)) {
        setPaymentStatus({
          type: 'failed',
          message: 'Payment was cancelled. No money was deducted.',
        });
      } else {
        setPaymentStatus({
          type: 'failed',
          message: result?.message || result?.data?.message || 'Payment transaction failed. Please try again.',
        });
      }
    } catch (err) {
      setPaymentStatus({
        type: 'pending',
        navOrderId: orderId,
        message: 'Unable to verify payment instantly. Please check your order status shortly.',
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      onClose();
      openLoginModal();
      return;
    }

    if (selectedTotalPayable <= 0 && paymentMethod !== 'COD') {
      alert('Cart total must be greater than zero.');
      return;
    }

    const resolvedBucketId =
      rawBucketData?.buy_now_bucket?._id ||
      rawBucketData?.buy_now_bucket?.bucket_id ||
      rawBucketData?.bucket_id ||
      rawBucketData?._id;

    if (!resolvedBucketId) {
      alert('Bucket reference not found. Please refresh and try again.');
      return;
    }

    setIsProcessingPayment(true);

    try {
      const cleanProjectId =
        typeof deliveryAddress?.project_id === 'object'
          ? deliveryAddress?.project_id?._id
          : deliveryAddress?.project_id || null;

      const payload = {
        amount: Math.round(selectedTotalPayable),
        customerId: customerId,
        hubId: customer?.hubId || 'default',
        projectId: cleanProjectId,
        bucketId: resolvedBucketId,
        paymentMethod: paymentMethod === 'COD' ? 'COD' : 'ONLINE',
        selected_delivery_type: selectedDeliveryOption?.type || selectedDeliveryType,
        delivery_type: selectedDeliveryOption?.type || selectedDeliveryType,
        pricing_token: rawBucketData?.pricing_token,
      };

      const res = await initiatePaymentMutation.mutateAsync(payload);
      const resData = res?.data?.data || res?.data || res;

      // 1. Cash On Delivery Flow
      if (paymentMethod === 'COD' || resData?.payment_mode === 'COD') {
        const orderId = resData?.order_id || resData?.orderId;
        setPaymentStatus({
          type: 'cod',
          navOrderId: orderId,
          message: 'Your order is confirmed! Pay in cash upon delivery.',
        });
        refetchBucketData();
        queryClient.invalidateQueries({ queryKey: ['customerOrders', customerId] });
        setIsProcessingPayment(false);
        return;
      }

      // 2. Identify Gateway (Preserving your exact working modal logic)
      const targetGateway = (resData?.gateway || resData?.provider || 'CASHFREE').toUpperCase();
      const currentOrderId = resData?.order_id || resData?.orderId;

      if (targetGateway === 'CASHFREE') {
        const { payment_session_id } = resData;
        if (!payment_session_id) throw new Error('Invalid Cashfree session received.');

        const cashfree = await load({ mode: "sandbox" });
        
        cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: "_modal" 
        }).then((result) => {
          if (result.error) {
            setPaymentStatus({ type: 'failed', message: result.error.message || 'Payment cancelled by user.'});
            setIsProcessingPayment(false);
          }
          console.log('cashfree, result', result);
          if (result.paymentDetails) {
            verifyBackendPaymentStatus(currentOrderId);
          }
        });

      } else if (targetGateway === 'PAYTM') {
        const { txnToken, mid, amount: serverAmount } = resData;
        if (!txnToken || !mid) throw new Error('Incomplete Paytm credentials returned.');

        const isProd = false;
        const scriptSrc = isProd
          ? `https://secure.paytmpayments.com/merchantpgpui/checkoutjs/merchants/${mid}.js`
          : `https://securegw-stage.paytm.in/merchantpgpui/checkoutjs/merchants/${mid}.js`;

        const script = document.createElement('script');
        script.src = scriptSrc;
        script.crossOrigin = "anonymous";
        script.onload = () => {
          const config = {
            "root": "",
            "flow": "DEFAULT",
            "data": {
              "orderId": currentOrderId,
              "token": txnToken,
              "tokenType": "TXN_TOKEN",
              "amount": serverAmount || selectedTotalPayable
            },
            "handler": {
              "transactionStatus": function(data) {
                window.Paytm.CheckoutJS.close();
                verifyBackendPaymentStatus(currentOrderId);
              },
              "notifyMerchant": function(eventName, data) {
                if (eventName === 'APP_CLOSED') {
                  setPaymentStatus({ type: 'failed', message: 'You closed the payment popup.' });
                  setIsProcessingPayment(false);
                }
              }
            }
          };
          window.Paytm.CheckoutJS.init(config).then(function() {
            window.Paytm.CheckoutJS.invoke();
          });
        };
        document.body.appendChild(script);

      } else {
        throw new Error(`Unsupported payment gateway: ${targetGateway}`);
      }

    } catch (err) {
      console.error('Instant checkout error:', err);
      setPaymentStatus({
        type: 'failed',
        message: err?.response?.data?.message || err?.message || 'Unable to complete checkout.',
      });
      setIsProcessingPayment(false);
    }
  };

  const handleOrderNavigation = async () => {
    if (paymentStatus.type === 'failed' || paymentStatus.type === 'cancelled') {
      setPaymentStatus(null);
      return;
    }
    console.log('handleOrderNavigation', paymentStatus);
    const targetTransactionId = paymentStatus.navOrderId || paymentStatus.displayOrderId;
    
    if (!targetTransactionId) {
      setPaymentStatus(null);
      onClose();
      return;
    }

    setIsNavigating(true);
    try {
      const response = await getOrderMutation.mutateAsync({
        transaction_order_id: targetTransactionId
      });
      console.log('response', response);
      const realOrderId = response?.data?.order_id || response?.order_id;
      
      setPaymentStatus(null);
      onClose();
      
      if (realOrderId) {
        navigate(`/consumer/account/orders/${realOrderId}`);
      } else {
        navigate(`/consumer/account/orders`);
      }
    } catch (err) {
      console.error("Failed to map transaction to order:", err);
      setPaymentStatus(null);
      onClose();
      navigate(`/consumer/account/orders`);
    } finally {
      setIsNavigating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
          {/* Top Header */}
          <div style={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h3 style={styles.headerTitle}>Bucket Items</h3>
              <span style={styles.itemCountBadge}>({displayItems.length})</span>
            </div>
            <button style={styles.closeBtn} onClick={onClose} type="button">
              <img src={X} alt="close" style={{ width: 14, height: 14 }} />
            </button>
          </div>

          {/* Scrollable Body */}
          <div style={styles.scrollBody}>
            {/* 1. Address Card (Authenticated Only) */}
            {isAuthenticated && (
              <div style={styles.addressCard}>
                <div style={styles.addressCardHeader}>
                  <span style={styles.addressTitle}>{addressDisplayName}</span>
                  <button
                    style={styles.changeAddressBtn}
                    onClick={() => setIsAddressModalOpen(true)}
                    type="button"
                  >
                    Change
                  </button>
                </div>
                <div style={styles.addressContent}>
                  {isAddressLoading ? (
                    <span style={{ fontSize: 12, color: '#707B8C' }}>Locating site address...</span>
                  ) : (
                    <p style={styles.addressText}>{formattedAddress}</p>
                  )}
                </div>
              </div>
            )}

            {isAuthenticated && !deliveryServiceable && (
              <div style={styles.unserviceableAlert}>
                <strong>Delivery not serviceable</strong>
                <p style={{ margin: '4px 0 0 0', fontSize: 12 }}>{deliveryFailureMessage}</p>
              </div>
            )}

            {/* 2. Delivery Options (Authenticated Only) */}
            {isAuthenticated && deliveryOptions.length > 0 && displayItems.length > 0 && (
              <div style={styles.deliveryCard}>
                <div style={styles.deliveryCardHeader}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: '700', color: '#2B2A29' }}>
                      Delivery options
                    </h4>
                    <span style={{ fontSize: 11, color: '#707B8C' }}>
                      Choose a convenient time for your order
                    </span>
                  </div>
                  <img src={Truck} alt="delivery" style={{ width: 20, height: 20 }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {deliveryOptions.map((opt) => {
                    const isSelected = selectedDeliveryOption?.type === opt.type;
                    const isFast = opt.type === 'FAST';
                    const charge = Number(opt.deliveryCharge) || 0;

                    return (
                      <div
                        key={opt.type}
                        style={{
                          ...styles.deliveryOptionItem,
                          ...(isSelected ? styles.deliveryOptionActive : {}),
                        }}
                        onClick={() => setSelectedDeliveryType(opt.type)}
                      >
                        <div style={styles.radioCircle}>
                          {isSelected && <div style={styles.radioDot} />}
                        </div>

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 13, fontWeight: '700', color: '#2B2A29' }}>
                                {isFast ? 'Fast delivery' : 'Scheduled delivery'}
                              </span>
                              {isFast && (
                                <div style={styles.fastEtaBadge}>
                                  <img src={Lightning} alt="fast" style={{ width: 11, height: 11 }} />
                                  <span>30 - 90 mins</span>
                                </div>
                              )}
                            </div>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: '700',
                                color: charge > 0 ? '#2B2A29' : '#1E8449',
                              }}
                            >
                              {charge > 0 ? `₹${charge.toFixed(2)}` : 'Free'}
                            </span>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                            {!isFast && <img src={Clock} alt="clock" style={{ width: 12, height: 12, opacity: 0.6 }} />}
                            <span style={{ fontSize: 11, color: '#707B8C' }}>
                              {opt.displayText || opt.slot?.label || 'Direct site delivery'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 3. Items List Container */}
            <div style={styles.itemsWrapper}>
              <div style={styles.itemsCountHeader}>
                <span>Items ({displayItems.length})</span>
              </div>

              {displayItems.length === 0 ? (
                <div style={styles.emptyCartBox}>
                  <img src={bucket} alt="Bucket" style={styles.bucketImg} />
                  <h4 style={{ margin: '8px 0 2px 0', color: '#2B2A29' }}>Your bucket is empty</h4>
                  <p style={{ margin: 0, fontSize: 12, color: '#707B8C' }}>Let's start building!</p>
                </div>
              ) : (
                displayItems.map((item) => (
                  <BucketItemCard
                    key={item.item_id || item.product_id || item._id}
                    item={item}
                    onUpdateQty={handleUpdateQty}
                    onRemove={handleRemoveItem}
                    onToggleSave={() => {}}
                  />
                ))
              )}
            </div>

            {displayItems.length > 0 && (
              <>
                {/* 4. Bill Summary Card */}
                <div style={styles.summaryCard}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: '700', color: '#2B2A29' }}>
                    Bill Summary
                  </h4>

                  <div style={styles.summaryRow}>
                    <span>Item Total</span>
                    <span style={{ fontWeight: '600' }}>
                      ₹{(isAuthenticated ? Number(billSummary?.sub_total || 0) : guestSubtotal).toFixed(2)}
                    </span>
                  </div>

                  {isAuthenticated ? (
                    <>
                      <div style={styles.summaryRow}>
                        <span>Tax</span>
                        <span style={{ fontWeight: '600' }}>₹{Number(billSummary?.total_tax || 0).toFixed(2)}</span>
                      </div>
                      <div style={styles.summaryRow}>
                        <span>Delivery charge</span>
                        <span
                          style={{
                            fontWeight: '600',
                            color: selectedDeliveryCharge > 0 ? '#2B2A29' : '#1E8449',
                          }}
                        >
                          {selectedDeliveryCharge > 0 ? `₹${selectedDeliveryCharge.toFixed(2)}` : 'Free'}
                        </span>
                      </div>
                      {billSummary?.discount_applied > 0 && (
                        <div style={styles.summaryRow}>
                          <span>Item discount</span>
                          <span style={{ color: '#1E8449', fontWeight: '600' }}>
                            -₹{Number(billSummary.discount_applied).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {billSummary?.wallet_balance > 0 && (
                        <div style={styles.summaryRow}>
                          <span>Wallet Applied</span>
                          <span style={{ color: '#1E8449', fontWeight: '600' }}>
                            -₹{Number(billSummary.wallet_balance).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div style={styles.receiptDivider} />
                      <div style={{ ...styles.summaryRow, fontSize: 15 }}>
                        <strong>Total Payable</strong>
                        <strong>₹{Number(selectedTotalPayable).toFixed(2)}</strong>
                      </div>

                      {billSummary?.discount_applied > 0 && (
                        <div style={styles.savingsPill}>
                          You saved ₹{Number(billSummary.discount_applied).toFixed(2)} on this order
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={styles.unauthNoticeBox}>
                      <span>
                        Log in to see your exact total. Applicable charges and discounts will be calculated based on your delivery site.
                      </span>
                    </div>
                  )}
                </div>

                {/* 5. Offers (Active if Authenticated, Locked if Guest) */}
                {isAuthenticated ? (
                  rawBucketData?.discounts && rawBucketData.discounts.discount > 0 ? (
                    <div style={styles.discountCardApplied}>
                      <div style={styles.discountIconBoxGreen}>
                        <img src={SealPercent} alt="%" style={{ width: 18, height: 18 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: 13, color: '#16A34A', display: 'block' }}>
                          {rawBucketData.discounts.discount_title || 'Applied Offer'}
                        </strong>
                        <span style={{ fontSize: 11, color: '#707B8C' }}>
                          ₹{rawBucketData.discounts.discount?.toFixed(2)} applied
                        </span>
                      </div>
                      <button
                        style={styles.removeDiscountBtn}
                        onClick={() => handleRemoveDiscount(rawBucketData.discounts.rule_code)}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div style={styles.discountCard} onClick={handleOpenDiscounts}>
                      <div style={styles.discountIconBox}>
                        <img src={SealPercent} alt="offer" style={{ width: 18, height: 18 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                          Apply Offer
                        </strong>
                        <span style={{ fontSize: 11, color: '#707B8C' }}>
                          Tap to view all available discount offers.
                        </span>
                      </div>
                      <img src={ArrowRight} alt="arrow" style={{ width: 12, height: 12 }} />
                    </div>
                  )
                ) : (
                  <div
                    style={styles.lockedCard}
                    onClick={() => {
                      onClose();
                      openLoginModal();
                    }}
                  >
                    <div style={styles.lockedIconBox}>
                      <span style={{ fontSize: 14 }}>🔒</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                        Coupons & Offers Locked
                      </strong>
                      <span style={{ fontSize: 11, color: '#707B8C' }}>
                        Log in to claim welcome discounts and promotional savings
                      </span>
                    </div>
                    <span style={styles.unlockActionText}>Unlock</span>
                  </div>
                )}

                {/* --- NEW: Brick Points Module --- */}
                {isAuthenticated && (
                  <div style={styles.brickPointsCard}>
                    {brickPointsEligible ? (
                      <div style={styles.bpRow}>
                        <div style={styles.bpIconCircle}>🪙</div>
                        <div style={{ flex: 1 }}>
                          <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                            {brickPointsEarned} Brick Points {brickPointsLocked ? '🔒 Locked' : 'Earned'}
                          </strong>
                          <span style={{ fontSize: 11, color: '#707B8C' }}>
                            {brickPointsLocked
                              ? 'Complete professional verification to unlock your points.'
                              : 'Points will be credited to your wallet after delivery.'}
                          </span>
                        </div>
                        {brickPointsLocked && (
                          <button 
                            style={styles.unlockPointsBtn} 
                            onClick={() => setIsPointsModalOpen(true)}
                          >
                            Unlock
                          </button>
                        )}
                      </div>
                    ) : (
                      <div style={styles.bpRow}>
                        <div style={styles.bpIconCircleMuted}>🪙</div>
                        <div style={{ flex: 1 }}>
                          <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                            Brick Points Program
                          </strong>
                          <span style={{ fontSize: 11, color: '#707B8C' }}>
                            Exclusive rewards for Professional Builders, Contractors, Architects & Skilled Professionals.
                          </span>
                        </div>
                        <button 
                          style={styles.unlockPointsBtn} 
                          onClick={() => setIsPointsModalOpen(true)}
                        >
                          Join Now
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* 6. Wallet (Authenticated Only) */}
                {isAuthenticated && availableWalletBalance > 0 && (
                  <div style={styles.walletCard}>
                    <div style={styles.walletIconBox}>
                      <img src={WalletIcon} alt="wallet" style={{ width: 18, height: 18 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                        Use Wallet Balance
                      </strong>
                      <span style={{ fontSize: 11, color: '#707B8C' }}>
                        Available: ₹{availableWalletBalance}
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      checked={useWallet}
                      onChange={handleWalletToggle}
                      style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#E9001E' }}
                    />
                  </div>
                )}

                {/* 7. Payment Options (Authenticated Only) */}
                {isAuthenticated && (
                  <div style={styles.paymentCard}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: 14, fontWeight: '700', color: '#2B2A29' }}>
                      Payment method
                    </h4>

                    <div
                      style={{
                        ...styles.paymentOption,
                        ...(paymentMethod === 'ONLINE' ? styles.paymentOptionActive : {}),
                      }}
                      onClick={() => setPaymentMethod('ONLINE')}
                    >
                      <input
                        type="radio"
                        name="payMethod"
                        checked={paymentMethod === 'ONLINE'}
                        onChange={() => setPaymentMethod('ONLINE')}
                        style={{ accentColor: '#E9001E' }}
                      />
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                          Pay online
                        </strong>
                        <span style={{ fontSize: 11, color: '#707B8C' }}>
                          UPI, Cards, Net Banking & Wallets powered by Cashfree
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        ...styles.paymentOption,
                        ...(paymentMethod === 'COD' ? styles.paymentOptionActive : {}),
                        ...(selectedTotalPayable > 10000 ? { opacity: 0.6 } : {}),
                      }}
                      onClick={() => {
                        if (selectedTotalPayable <= 10000) setPaymentMethod('COD');
                      }}
                    >
                      <input
                        type="radio"
                        name="payMethod"
                        checked={paymentMethod === 'COD'}
                        disabled={selectedTotalPayable > 10000}
                        onChange={() => setPaymentMethod('COD')}
                        style={{ accentColor: '#E9001E' }}
                      />
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: 13, color: '#2B2A29', display: 'block' }}>
                          Cash on Delivery
                        </strong>
                        <span style={{ fontSize: 11, color: '#707B8C' }}>
                          {selectedTotalPayable > 10000
                            ? 'COD is only available for orders under ₹10,000'
                            : 'Pay in cash when materials are unloaded at site'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 8. Trust Banner (Authenticated Only) */}
                {isAuthenticated && (
                  <div style={styles.trustBanner}>
                    <img src={ShieldCheck} alt="shield" style={{ width: 16, height: 16 }} />
                    <span>100% Secure Payments • GST Invoice Emailed</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sticky Drawer Footer */}
          {displayItems.length > 0 && (
            <div style={styles.footerWrapper}>
              {isAuthenticated && isBulkEligible && (
                <div
                  style={{
                    ...styles.bulkStrip,
                    backgroundColor: isOverLimit ? '#FFFBEB' : '#EFF6FF',
                    borderColor: isOverLimit ? '#FDE68A' : '#BFDBFE',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: '700',
                      color: isOverLimit ? '#92400E' : '#1E40AF',
                    }}
                  >
                    {isOverLimit
                      ? 'Request for Quote required for orders exceeding ₹1 Lakh'
                      : 'Order eligible for Request for Quote (RFQ)'}
                  </span>
                  <button
                    style={styles.bulkStripBtn}
                    onClick={() => {
                      onClose();
                      navigate('/consumer/rfq');
                    }}
                    type="button"
                  >
                    Request Quote
                  </button>
                </div>
              )}

              <div style={styles.footerMainRow}>
                <div>
                  <span style={{ fontSize: 11, color: '#707B8C', display: 'block' }}>Total</span>
                  <span style={{ fontSize: 18, fontWeight: '800', color: '#2B2A29' }}>
                    ₹
                    {(isAuthenticated
                      ? Number(selectedTotalPayable)
                      : guestSubtotal
                    ).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>

                {isAuthenticated ? (
                  <button
                    style={{
                      ...styles.payNowBtn,
                      opacity: isProcessingPayment ? 0.7 : 1,
                    }}
                    disabled={isProcessingPayment}
                    onClick={handleCheckout}
                    type="button"
                  >
                    {isProcessingPayment
                      ? 'Processing...'
                      : paymentMethod === 'COD'
                      ? 'Place COD Order'
                      : 'Pay Now'}
                  </button>
                ) : (
                  <button
                    style={styles.payNowBtn}
                    onClick={() => {
                      onClose();
                      openLoginModal();
                    }}
                    type="button"
                  >
                    Login to Proceed
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Payment Status Overlay */}
          {paymentStatus && (
            <div style={styles.statusModalOverlay}>
              <div style={styles.statusCard}>
                <div
                  style={{
                    ...styles.statusIconCircle,
                    backgroundColor: paymentStatus.type === 'failed' ? '#FEE2E2' : '#DCFCE7',
                  }}
                >
                  <img
                    src={paymentStatus.type === 'failed' ? X : ShieldCheck}
                    alt="status"
                    style={{ width: 28, height: 28 }}
                  />
                </div>
                <h3 style={{ margin: '10px 0 6px 0', fontSize: 18, color: '#2B2A29' }}>
                  {paymentStatus.type === 'failed' ? 'Payment Failed' : 'Order Placed!'}
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#636C78', textAlign: 'center' }}>
                  {paymentStatus.message}
                </p>
                {paymentStatus.displayOrderId && (
                  <span style={{ fontSize: 12, fontWeight: '700', color: '#2B2A29', marginBottom: 16 }}>
                    Order ID: {paymentStatus.displayOrderId}
                  </span>
                )}
                <button
                  style={{
                    ...styles.statusActionBtn,
                    opacity: isNavigating ? 0.7 : 1
                  }}
                  disabled={isNavigating}
                  onClick={handleOrderNavigation}
                  type="button"
                >
                  {isNavigating 
                    ? 'Loading Order...' 
                    : paymentStatus.type === 'failed' 
                      ? 'Retry Payment' 
                      : 'View Order'
                  }
                </button>
              </div>
            </div>
          )}

          {/* Discount Modal */}
          {isDiscountModalOpen && (
            <div style={styles.statusModalOverlay} onClick={() => setIsDiscountModalOpen(false)}>
              <div style={styles.couponModalBox} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <h4 style={{ margin: 0, fontSize: 16, color: '#2B2A29' }}>Apply Offers</h4>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={() => setIsDiscountModalOpen(false)}
                    type="button"
                  >
                    ✕
                  </button>
                </div>

                {isEvaluatingDiscounts ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#707B8C' }}>
                    Evaluating active offers...
                  </div>
                ) : evaluatedDiscounts.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#707B8C' }}>
                    No coupons applicable for the current items in your bucket.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {evaluatedDiscounts.map((coupon, idx) => (
                      <div key={coupon.code || idx} style={styles.couponTile}>
                        <div>
                          <strong style={{ fontSize: 13, color: '#E9001E' }}>{coupon.code}</strong>
                          <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#2B2A29' }}>
                            {coupon.title || coupon.sub_text}
                          </p>
                        </div>
                        <button
                          style={styles.applyCouponBtn}
                          onClick={() => handleApplyDiscount(coupon)}
                          type="button"
                        >
                          Apply
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
       {/* Brick Points Professional Verification Modal */}
          {isPointsModalOpen && (
            <div style={styles.statusModalOverlay} onClick={() => setIsPointsModalOpen(false)}>
              <div style={styles.couponModalBox} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <h4 style={{ margin: 0, fontSize: 16, color: '#2B2A29' }}>Unlock Brick Points</h4>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={() => setIsPointsModalOpen(false)}
                    type="button"
                  >
                    ✕
                  </button>
                </div>
                
                <div style={{ marginBottom: 16 }}>
                  <span style={{ fontSize: 12, color: '#707B8C' }}>
                    Provide your professional details to unlock and earn Brick Points on every order.
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: '600', marginBottom: 4, display: 'block', color: '#2B2A29' }}>
                      Customer Type <span style={{ color: '#E9001E' }}>*</span>
                    </label>
                    <select
                      value={profType}
                      onChange={e => setProfType(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #CBD5E1', outline: 'none' }}
                    >
                      <option value="">Select Category</option>
                      <option value="Professional Builder">Professional Builder</option>
                      <option value="Skilled Professional">Skilled Professional</option>
                      <option value="Contractor">Contractor</option>
                      <option value="Supervisor">Supervisor</option>
                      <option value="Architect">Architect</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: 12, fontWeight: '600', marginBottom: 4, display: 'block', color: '#2B2A29' }}>
                      GSTIN (Recommended)
                    </label>
                    <input
                      type="text"
                      value={gstin}
                      onChange={e => setGstin(e.target.value)}
                      placeholder="Enter 15-digit GSTIN"
                      style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #CBD5E1', outline: 'none', textTransform: 'uppercase' }}
                      maxLength={15}
                    />
                  </div>

                  <button
                    style={{ 
                      backgroundColor: profType ? '#E9001E' : '#E2E8F0', 
                      color: profType ? '#FFFFFF' : '#94A3B8', 
                      border: 'none', 
                      borderRadius: 8, 
                      padding: '12px', 
                      fontWeight: '700', 
                      cursor: profType ? 'pointer' : 'not-allowed', 
                      marginTop: 8 
                    }}
                    onClick={handleVerifyPointsProfile}
                    disabled={!profType}
                  >
                    Submit for Verification
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {isAuthenticated && (
        <DeliveryAddressModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          onAddNewAddress={(loc) => navigate('/consumer/account/delivery/new', { state: loc })}
        />
      )}
    </>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'flex-end',
    backdropFilter: 'blur(2px)',
    overscrollBehavior: 'contain',
  },
  drawer: {
    width: '100%',
    maxWidth: 460,
    height: '100vh',
    backgroundColor: '#FAF9F6',
    boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    animation: 'slideLeft 0.25s ease-out',
  },
  header: {
    padding: '16px 20px',
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E2E8F0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: '700',
    color: '#2B2A29',
  },
  itemCountBadge: {
    fontSize: 14,
    color: '#707B8C',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
  },
  scrollBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 18px 24px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    overscrollBehavior: 'contain',
  },
  addressCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '74px',
    flexShrink: 0,
    overflow: 'hidden',
  },
  addressCardHeader: {
    backgroundColor: '#F8F6F2',
    padding: '10px 14px',
    borderBottom: '1px solid #CBD5E1',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  addressTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#2B2A29',
  },
  changeAddressBtn: {
    background: 'none',
    border: 'none',
    color: '#E9001E',
    fontSize: 12,
    fontWeight: '700',
    cursor: 'pointer',
  },
  addressContent: {
    padding: '12px 14px',
  },
  addressText: {
    margin: 0,
    fontSize: 12,
    color: '#2B2A29',
    lineHeight: '16px',
  },
  unserviceableAlert: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    color: '#B91C1C',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    flexShrink: 0,
  },
  deliveryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: 14,
    flexShrink: 0,
  },
  deliveryCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  deliveryOptionItem: {
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
  },
  deliveryOptionActive: {
    borderColor: '#E9001E',
    backgroundColor: '#FFF5F5',
  },
  radioCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    border: '1.5px solid #CBD5E1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#E9001E',
  },
  fastEtaBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    backgroundColor: '#DCFCE7',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: '700',
    color: '#166534',
  },
  itemsWrapper: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'visible',
    flexShrink: 0,
  },
  itemsCountHeader: {
    padding: '10px 14px',
    backgroundColor: '#F8F6F2',
    borderBottom: '1px solid #CBD5E1',
    borderTopLeftRadius: 9,
    borderTopRightRadius: 9,
    fontSize: 12,
    fontWeight: '700',
    color: '#2B2A29',
    display: 'block',
  },
  emptyCartBox: {
    padding: '36px 16px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  bucketImg: {
    width: 32,
    height: 32,
    objectFit: 'contain',
    display: 'block',
  },
  summaryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    color: '#2B2A29',
  },
  receiptDivider: {
    height: 1,
    backgroundColor: '#CBD5E1',
    margin: '4px 0',
  },
  savingsPill: {
    backgroundColor: '#DCFCE7',
    color: '#166534',
    fontSize: 11,
    fontWeight: '700',
    padding: '6px 10px',
    borderRadius: 6,
    textAlign: 'center',
    marginTop: 4,
  },
  unauthNoticeBox: {
    backgroundColor: '#FFF8F0',
    border: '1px solid #FFE4C8',
    borderRadius: 8,
    padding: '10px 12px',
    color: '#9A5B18',
    fontSize: 12,
    lineHeight: '16px',
    marginTop: 8,
  },
  brickPointsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #FCD34D',
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flexShrink: 0,
    overflow: 'hidden'
  },
  bpRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  bpIconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FEF3C7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16
  },
  bpIconCircleMuted: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    opacity: 0.7
  },
  unlockPointsBtn: {
    background: 'none',
    border: '1px solid #E9001E',
    color: '#E9001E',
    fontSize: 11,
    fontWeight: '700',
    padding: '6px 10px',
    borderRadius: 6,
    cursor: 'pointer',
  },
  discountCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    flexShrink: 0,
  },
  discountCardApplied: {
    backgroundColor: '#F0FDF4',
    borderRadius: 10,
    border: '1px solid #86EFAC',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  discountIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#F5F3EF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  discountIconBoxGreen: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#DCFCE7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeDiscountBtn: {
    background: 'none',
    border: 'none',
    color: '#DC2626',
    fontSize: 12,
    fontWeight: '700',
    cursor: 'pointer',
  },
  lockedCard: {
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    border: '1px dashed #CBD5E1',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    flexShrink: 0,
  },
  lockedIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#EDF2F7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unlockActionText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#E9001E',
  },
  walletCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  walletIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#FEF3C7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0,
  },
  paymentOption: {
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
  },
  paymentOptionActive: {
    borderColor: '#E9001E',
    backgroundColor: '#FFF5F5',
  },
  trustBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: '#16A34A',
    fontSize: 11,
    fontWeight: '600',
    padding: '8px 0',
    flexShrink: 0,
  },
  footerWrapper: {
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #E2E8F0',
    padding: '12px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flexShrink: 0,
  },
  bulkStrip: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 6,
    padding: '6px 10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bulkStripBtn: {
    background: 'none',
    border: 'none',
    color: '#0F4392',
    fontWeight: '700',
    fontSize: 11,
    cursor: 'pointer',
  },
  footerMainRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  payNowBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 14,
    fontWeight: '700',
    cursor: 'pointer',
    flex: 1,
  },
  statusModalOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  statusCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 320,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statusIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  statusActionBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    width: '100%',
    fontWeight: '700',
    cursor: 'pointer',
  },
  couponModalBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: '100%',
    maxWidth: 360,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  couponTile: {
    border: '1px dashed #CBD5E1',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  applyCouponBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    padding: '6px 12px',
    fontSize: 11,
    fontWeight: '700',
    cursor: 'pointer',
  },
};

/* const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'flex-end',
    backdropFilter: 'blur(2px)',
    overscrollBehavior: 'contain',
  },
  drawer: {
    width: '100%',
    maxWidth: 460,
    height: '100vh',
    backgroundColor: '#FAF9F6',
    boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    animation: 'slideLeft 0.25s ease-out',
  },
  header: {
    padding: '16px 20px',
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E2E8F0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: '700',
    color: '#2B2A29',
  },
  itemCountBadge: {
    fontSize: 14,
    color: '#707B8C',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
  },
  scrollBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 18px 24px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    overscrollBehavior: 'contain',
  },
  addressCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '74px',
    flexShrink: 0,
    overflow: 'hidden',
  },
  addressCardHeader: {
    backgroundColor: '#F8F6F2',
    padding: '10px 14px',
    borderBottom: '1px solid #CBD5E1',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  addressTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#2B2A29',
  },
  changeAddressBtn: {
    background: 'none',
    border: 'none',
    color: '#E9001E',
    fontSize: 12,
    fontWeight: '700',
    cursor: 'pointer',
  },
  addressContent: {
    padding: '12px 14px',
  },
  addressText: {
    margin: 0,
    fontSize: 12,
    color: '#2B2A29',
    lineHeight: '16px',
  },
  unserviceableAlert: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    color: '#B91C1C',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    flexShrink: 0,
  },
  deliveryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: 14,
    flexShrink: 0,
  },
  deliveryCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  deliveryOptionItem: {
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
  },
  deliveryOptionActive: {
    borderColor: '#E9001E',
    backgroundColor: '#FFF5F5',
  },
  radioCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    border: '1.5px solid #CBD5E1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#E9001E',
  },
  fastEtaBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    backgroundColor: '#DCFCE7',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: '700',
    color: '#166534',
  },
  itemsWrapper: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'visible',
    flexShrink: 0,
  },
  itemsCountHeader: {
    padding: '10px 14px',
    backgroundColor: '#F8F6F2',
    borderBottom: '1px solid #CBD5E1',
    borderTopLeftRadius: 9,
    borderTopRightRadius: 9,
    fontSize: 12,
    fontWeight: '700',
    color: '#2B2A29',
    display: 'block',
  },
  emptyCartBox: {
    padding: '36px 16px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  bucketImg: {
    width: 32,
    height: 32,
    objectFit: 'contain',
    display: 'block',
  },
  summaryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    color: '#2B2A29',
  },
  receiptDivider: {
    height: 1,
    backgroundColor: '#CBD5E1',
    margin: '4px 0',
  },
  savingsPill: {
    backgroundColor: '#DCFCE7',
    color: '#166534',
    fontSize: 11,
    fontWeight: '700',
    padding: '6px 10px',
    borderRadius: 6,
    textAlign: 'center',
    marginTop: 4,
  },
  unauthNoticeBox: {
    backgroundColor: '#FFF8F0',
    border: '1px solid #FFE4C8',
    borderRadius: 8,
    padding: '10px 12px',
    color: '#9A5B18',
    fontSize: 12,
    lineHeight: '16px',
    marginTop: 8,
  },
  discountCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    flexShrink: 0,
  },
  discountCardApplied: {
    backgroundColor: '#F0FDF4',
    borderRadius: 10,
    border: '1px solid #86EFAC',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  discountIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#F5F3EF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  discountIconBoxGreen: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#DCFCE7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeDiscountBtn: {
    background: 'none',
    border: 'none',
    color: '#DC2626',
    fontSize: 12,
    fontWeight: '700',
    cursor: 'pointer',
  },
  lockedCard: {
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    border: '1px dashed #CBD5E1',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    flexShrink: 0,
  },
  lockedIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#EDF2F7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unlockActionText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#E9001E',
  },
  walletCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  walletIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#FEF3C7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #CBD5E1',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0,
  },
  paymentOption: {
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
  },
  paymentOptionActive: {
    borderColor: '#E9001E',
    backgroundColor: '#FFF5F5',
  },
  trustBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: '#16A34A',
    fontSize: 11,
    fontWeight: '600',
    padding: '8px 0',
    flexShrink: 0,
  },
  footerWrapper: {
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #E2E8F0',
    padding: '12px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flexShrink: 0,
  },
  bulkStrip: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 6,
    padding: '6px 10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bulkStripBtn: {
    background: 'none',
    border: 'none',
    color: '#0F4392',
    fontWeight: '700',
    fontSize: 11,
    cursor: 'pointer',
  },
  footerMainRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  payNowBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 14,
    fontWeight: '700',
    cursor: 'pointer',
    flex: 1,
  },
  statusModalOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  statusCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 320,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statusIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  statusActionBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    width: '100%',
    fontWeight: '700',
    cursor: 'pointer',
  },
  couponModalBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: '100%',
    maxWidth: 360,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  couponTile: {
    border: '1px dashed #CBD5E1',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  applyCouponBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    padding: '6px 12px',
    fontSize: 11,
    fontWeight: '700',
    cursor: 'pointer',
  },
}; */